import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Constant } from "../../../../helper/constant";
import SalesFilter from "../../common_component/salesFilter";
import SalesClinicData from "./component/salesClinicData";
import { getInitialDate } from "../../../../helper/formatDate";
import SalesSummary from "../../common_component/salesSummary";
import SalesClinicReceipt from "./component/salesClinicReceipt";
import ModalDetailClinicSales from "./component/modalDetailClinicSales";
import PaginationFooter from "../../../../components/footer/paginationFooter";

const ClinicSales = () => {
  // =====================
  // Constant Definition
  // =====================

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [transactionData, setTransactionData] = useState([]);
  const [summaryTransaction, setSummaryTransaction] = useState(null);
  const [filterData, setFilterData] = useState(null);
  const [transactionToPrint, setTransactionToPrint] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [printTrigger, setPrintTrigger] = useState(false);
  const [isShowDetailTransactionModal, setIsShowDetailTransactionModal] =
    useState(false);
  const receiptRef = useRef();
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const userData = JSON.parse(localStorage.getItem("user_data"));

  // =====================
  // Method Definition
  // =====================

  const getTransactionDate = () => {
    if (filterData !== null) {
      return filterData.date;
    } else {
      return getInitialDate("daily");
    }
  };

  const onClickPrintTransaction = (transactionData) => {
    setTransactionToPrint(transactionData);
    setPrintTrigger((prev) => !prev);
  };

  const onOpenDetailTransaction = (transactionData) => {
    console.log(transactionData);
    setSelectedTransaction(transactionData);
    setIsShowDetailTransactionModal(true);
  };

  const onCloseModalDetailTransaction = () => {
    setIsShowDetailTransactionModal(false);
    setSelectedTransaction(null);
  };

  const onDeleteClinicTransaction = (idToDelete) => {
    deleteTransaction(idToDelete);
  };

  // Custom print function
  const handlePrint = useCallback(async () => {
    if (receiptRef.current) {
      const printWindow = window.open("", "_blank");

      // Write the HTML structure and CSS link
      printWindow.document.write(`
        <html>
          <head>
            <title>Resep Klinik ${transactionToPrint.medical_transaction_invoice_number}</title>
            <link rel="stylesheet" href="/css/productReceipt/salesReceipt.css">
          </head>
          <body>
            <div class="receipt">${receiptRef.current.innerHTML}</div>
          </body>
        </html>
      `);

      // Close the document to ensure styles are loaded
      printWindow.document.close();

      // Add a small delay to ensure styles are applied before printing
      setTimeout(() => {
        printWindow.print();
      }, 500); // Adjust this timeout if necessary
    }
  }, [transactionToPrint]);

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // ==========================
  // Network Request Definition
  // ==========================

  const getInitialTransaction = useCallback(async () => {
    try {
      setIsLoading(true);

      let url;
      if (filterData == null) {
        const params = new URLSearchParams({
          date: getInitialDate("daily"),
          page: page,
        }).toString();

        url = `${Constant.BASE_API_URL}/tindakan-medis-transaksi/daily?${params}`;
      } else {
        const params = new URLSearchParams({
          date: filterData.date,
          shift: filterData.shift.index || "",
          doctor_id: filterData.doctor.doctor_id,
          page: page,
        }).toString();

        url = `${Constant.BASE_API_URL}/tindakan-medis-transaksi/daily?${params}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;

      if (!data.medical_transactions) {
        setTransactionData([]);
      } else {
        setTransactionData(data.medical_transactions);
        setSummaryTransaction(data.summary);
      }
      setTotalPage(data.total_pages);
    } catch (error) {
      setTransactionData([]);
      setSummaryTransaction(null);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, filterData, page]);

  const deleteTransaction = async (medicalTransactionId) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        Constant.BASE_API_URL +
          "/tindakan-medis-transaksi/" +
          medicalTransactionId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        },
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getInitialTransaction();
    }
  };

  const downloadInactivePatients = async () => {
    try {
      setIsDownloading(true);
      const response = await fetch(
        `${Constant.BASE_API_URL}/tindakan-medis-transaksi/download-inactive-patients`,
        {
          method: "GET",
          headers: {
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        },
      );

      if (!response.ok) {
        alert("Gagal mendownload data pasien tidak aktif");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Pasien_Tidak_Aktif_6_Bulan.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Terjadi kesalahan sistem saat mengunduh data");
    } finally {
      setIsDownloading(false);
    }
  };

  // =====================
  // Hook Definition
  // =====================

  useEffect(() => {
    getInitialTransaction();
  }, [getInitialTransaction]);

  useEffect(() => {
    if (transactionToPrint) {
      handlePrint();
    }
  }, [transactionToPrint, printTrigger, handlePrint]);

  // =====================
  // Layout
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Penjualan Harian</h1>
      <SalesFilter typeOfSales={"daily"} setFilterData={setFilterData} />

      {userData != null &&
        (userData.staff_system_role === "ADMIN" ||
          userData.staff_system_role === "MANAGER") && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              margin: "15px 0",
            }}
          >
            <button
              type="button"
              disabled={isDownloading}
              onClick={downloadInactivePatients}
              style={{
                padding: "8px 16px",
                backgroundColor: isDownloading ? "#cccccc" : "#28a745",
                color: "#ffffff",
                border: "none",
                borderRadius: "4px",
                cursor: isDownloading ? "not-allowed" : "pointer",
                fontWeight: "bold",
              }}
            >
              {isDownloading
                ? "Mengunduh..."
                : "Download Pasien Tidak Aktif (H-6 Bulan)"}
            </button>
          </div>
        )}

      <SalesClinicData
        transactionDate={getTransactionDate()}
        transactionData={transactionData}
        onClickPrintTransaction={onClickPrintTransaction}
        onOpenDetailTransaction={onOpenDetailTransaction}
        onDeleteClinicTransaction={onDeleteClinicTransaction}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      <SalesSummary summaryData={summaryTransaction} />
      <ModalDetailClinicSales
        isShowModal={isShowDetailTransactionModal}
        clinicTransaction={selectedTransaction}
        onCloseModal={onCloseModalDetailTransaction}
      />

      <div style={{ display: "none" }}>
        <SalesClinicReceipt
          ref={receiptRef}
          transactionData={transactionToPrint}
        />
      </div>
    </div>
  );
};

export default ClinicSales;
